/** Where a value sits in the structure around it: the field it hangs under,
 *  or null for the value the walk was handed. */
export type DeepSlot = { key: string | null }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * A structure with some of its nodes rewritten, sharing everything the rewrite
 * did not touch.
 *
 * `visit` answers for one node: a value to put in its place, or `undefined` to
 * carry on into it. What comes back reuses every branch that did not change —
 * the same object, not a copy of it — which is what lets a React Query cache
 * be rewritten without re-rendering the readers of the parts that stayed.
 *
 * The walk exists because the lists a write is about sit at different depths in
 * different responses: a question hangs as deep as its group, a measure's
 * pictures one field down. Naming the path per response would be one path per
 * endpoint; this asks each node instead.
 */
export function mapDeep<T>(
  root: T,
  visit: (node: unknown, slot: DeepSlot) => unknown,
): T {
  const walk = (node: unknown, slot: DeepSlot): unknown => {
    const written = visit(node, slot)
    if (written !== undefined) return written

    if (Array.isArray(node)) {
      let changed = false
      const out = node.map(item => {
        const next = walk(item, slot)
        if (next !== item) changed = true
        return next
      })
      return changed ? out : node
    }

    if (!isRecord(node)) return node

    let out: Record<string, unknown> | null = null
    for (const key in node) {
      const value = node[key]
      const next = walk(value, { key })
      if (next === value) {
        if (out) out[key] = value
        continue
      }
      // Copied on the first child that moved, and only then: a response of a
      // thousand nodes is rebuilt down the one path that changed.
      out ??= { ...node }
      out[key] = next
    }
    return out ?? node
  }

  return walk(root, { key: null }) as T
}
